import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import EntitlementsAccountsTable from "components/pages/EntitlementsPage/components/EntitlementsAccountsTable";
import EntitlementsProfilesTable from "components/pages/EntitlementsPage/components/EntitlementsProfilesTable";
import EntitlementsTable from "components/pages/EntitlementsPage/components/EntitlementsTable";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { InfoBanner } from "components/shared/views/ConfirmBannerView";
import { PrimaryButton, SecondaryButton, UICheckbox } from "components/ui";
import Classes, { Account, Profile } from "model";
import React from "react";
import "./styles.css";
import "./mobile.css";


/**
 * @function EntitlementsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for EntitlementsPage
 */
export default function EntitlementsPage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const data = { ...getDefaultData(context), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";
    const { goStep } = useWorkflow() ?? {};

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        cancel: () => goStep(0),

        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "userAccount", prop ], value)
            .submit(setState);
        },

        onSave: () => {
            window.scrollTo(0, 0);
            goStep(0, { data: { entitlementsUpdated: true, selectedUserAccountID: data.userAccount.id } });
        }
    });

    return (
        <PageContainer className={"entitlements-page"+className} state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = (controller) => {
    return {
        isEditing: false,
        selectedUserAccountID: undefined,
        accounts: Account.initAll(),
        profiles: Profile.initAll(),
        userAccount: BCMSDefaultModel.userAccount(),
    };
};
  
  export const getDefaultUI = () => ({
    // no ui changes for now
    userAddedBanner: { isActive: true }
  });

  function PageContent(props) {
    const { data, ui, cancel, onChange, onSave } = useStateManager();
    const userAccount = data.userAccount ?? {};
    const accounts = data.accounts ?? [];
    const profiles = data.profiles ?? [];

    return (
        <PageContentView>
            <CenterContentContainer>
                { ui.userAddedBanner.isActive && 
                <InfoBanner text={`The user "${ userAccount.fullName }" has been added. On this screen, please set the entitlements for the user.`} />
                }
                <ContentHeaderView text={`Entitlements for ${ userAccount.fullName }`} />
                <FormContainer className="small-form-width">
                    <FormRowView className="state-row" text="Copy Entitlements From">
                        <DisabledPrompt>
                            <CustomSelect value={ "No User Selected" } dataPoint="Copy_Entitlements_From_Select">
                                <CustomSelectItem value="No User Selected">No User Selected</CustomSelectItem>
                            </CustomSelect>
                        </DisabledPrompt>
                    </FormRowView>
                </FormContainer>
                <div className="ui-checkbox-container">
                    <DisabledPrompt>
                        <UICheckbox value={ userAccount.isABusinessAdministrator } dataPoint="User_Is_Business_Admin" onChange={ onChange("isABusinessAdministrator") } label="This user is a business administrator. (An administrator is automatically granted all available entitlements.)" />
                    </DisabledPrompt>
                </div>
                { !userAccount.isABusinessAdministrator &&
                    <div className="entitlements-table-container">
                        <EntitlementsTable />
                        <EntitlementsAccountsTable accounts={ accounts } />
                        <EntitlementsProfilesTable profiles={ profiles } />
                    </div>
                }
                <div className="button-container flex-container flex-horizontal align-center ">
                    <SecondaryButton text="Cancel" dataPoint="Cancel_Button" onClick={ cancel } />
                    <PrimaryButton text="Save" dataPoint="Save_Button" onClick={ onSave } />
                </div>
            </CenterContentContainer>
        </PageContentView>
    );
}